import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import NewCategoryModal from './NewCategoryModal'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const { 
  FiX, FiPlus, FiBook, FiUser, FiImage, FiTag, FiHash, 
  FiLoader, FiUpload, FiEdit3 
} = FiIcons

const AddBookModal = ({ isOpen, onClose, onAdd, onUpdate, categories = [], editBook = null }) => {
  const [loading, setLoading] = useState(false)
  const [imageMode, setImageMode] = useState('upload')
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [isNewCatModalOpen, setIsNewCatModalOpen] = useState(false)
  const [localCategories, setLocalCategories] = useState([])
  
  const fileInputRef = useRef(null)
  
  const initialForm = {
    title: '', 
    author: '', 
    isbn: '', 
    category: '', 
    published_year: new Date().getFullYear(), 
    description: '', 
    cover_image: '', 
    tags: '', 
    total_copies: 1
  }
  
  const [formData, setFormData] = useState(initialForm)

  // 1. Prepare stable category list
  useEffect(() => {
    const base = [...new Set([...categories, 'Fiction', 'Non-Fiction', 'History', 'Memoir', 'Poetry', 'Science'])]
    setLocalCategories(base.sort())
  }, [categories])

  // 2. Only initialize/reset form when modal opens or the edit target changes
  useEffect(() => {
    if (isOpen) {
      if (editBook) {
        setFormData({
          ...editBook,
          tags: Array.isArray(editBook.tags) ? editBook.tags.join(',') : (editBook.tags || ''),
          total_copies: editBook.total_copies || 1,
          published_year: editBook.published_year || ''
        })
        setPreviewUrl(editBook.cover_image || '')
        setImageMode(editBook.cover_image ? 'url' : 'upload')
      } else {
        // Reset to initial state for new book
        // We calculate the initial category here once
        const defaultCategory = localCategories.length > 0 ? localCategories[0] : ''
        setFormData({
          ...initialForm,
          category: defaultCategory
        })
        setPreviewUrl('')
        setImageMode('upload')
        setSelectedFile(null)
      }
    }
  }, [isOpen, editBook]) // Removed localCategories from dependencies to prevent reset loop

  const handleAddNewCategory = (newCat) => {
    if (!localCategories.includes(newCat)) {
      setLocalCategories(prev => [...prev, newCat].sort())
    }
    // Explicitly update the form selection to the newly created category
    setFormData(prev => ({ ...prev, category: newCat }))
    toast.success(`Category "${newCat}" added`)
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size too large (max 2MB)')
        return
      }
      setSelectedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setPreviewUrl(reader.result)
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.category) {
      toast.error("Please select a category")
      return
    }
    setLoading(true)
    try {
      const action = editBook ? onUpdate : onAdd
      const result = await action(
        editBook ? editBook.id : formData, 
        editBook ? formData : (imageMode === 'upload' ? selectedFile : null),
        editBook ? (imageMode === 'upload' ? selectedFile : null) : undefined
      )
      if (result.success) {
        toast.success(editBook ? 'Volume updated!' : `"${formData.title}" registered!`)
        onClose()
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 md:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-space_indigo/80 backdrop-blur-sm" />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.95, y: 20 }} 
            className="bg-white w-full max-w-5xl h-full max-h-[90vh] md:h-auto rounded-[2rem] shadow-2xl relative z-10 overflow-hidden border-2 border-white-400 flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b-2 border-white-400 bg-white-500 flex items-center justify-between shrink-0">
              <h2 className="text-base md:text-xl font-black text-space_indigo uppercase tracking-tight flex items-center">
                <div className={`${editBook ? 'bg-space_indigo' : 'bg-dark_cyan'} p-1.5 md:p-2 rounded-lg md:rounded-xl mr-3`}>
                  <SafeIcon icon={editBook ? FiEdit3 : FiPlus} className="w-4 h-4 text-white" />
                </div>
                {editBook ? 'Modify Volume' : 'Volume Registration'}
              </h2>
              <button onClick={onClose} className="p-2 text-white-300 hover:text-hot_berry transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  {/* Image Section */}
                  <div className="w-full lg:w-72 shrink-0 flex flex-col items-center space-y-4">
                    <div className="w-full max-w-[180px] lg:max-w-none aspect-[3/4] bg-white-500 rounded-3xl border-2 border-dashed border-white-400 overflow-hidden relative group shadow-inner">
                      {previewUrl ? <img src={previewUrl} className="w-full h-full object-cover" alt="Cover Preview" /> : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white-300">
                          <SafeIcon icon={FiImage} className="w-10 h-10 mb-2 opacity-30" />
                          <span className="text-[10px] font-black uppercase tracking-widest opacity-50">No Cover</span>
                        </div>
                      )}
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute inset-0 bg-space_indigo/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                        <div className="bg-white p-3 rounded-2xl shadow-xl">
                          <SafeIcon icon={FiUpload} className="w-5 h-5 text-dark_cyan" />
                        </div>
                      </button>
                    </div>
                    <div className="flex bg-white-500 rounded-2xl p-1 border-2 border-white-400 w-full max-w-[240px]">
                      {['upload', 'url'].map(mode => (
                        <button key={mode} type="button" onClick={() => setImageMode(mode)} className={`flex-1 py-1.5 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all ${imageMode === mode ? 'bg-dark_cyan text-white shadow-lg' : 'text-white-300'}`}>
                          {mode}
                        </button>
                      ))}
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                  </div>

                  {/* Form Section */}
                  <div className="flex-1 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Title</label>
                          <div className="relative">
                            <SafeIcon icon={FiBook} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                            <input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Category</label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <SafeIcon icon={FiTag} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                              <select 
                                required 
                                value={formData.category} 
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
                                className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs appearance-none outline-none"
                              >
                                {localCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                              </select>
                            </div>
                            <button 
                              type="button" 
                              onClick={() => setIsNewCatModalOpen(true)}
                              className="bg-white-500 border-2 border-white-400 p-3 rounded-xl hover:border-dark_cyan hover:text-dark_cyan transition-all shrink-0"
                            >
                              <SafeIcon icon={FiPlus} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Author</label>
                          <div className="relative">
                            <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
                            <input required value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Year</label>
                            <input type="number" value={formData.published_year} onChange={(e) => setFormData({ ...formData, published_year: e.target.value })} className="w-full px-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Qty</label>
                            <input type="number" min="1" required value={formData.total_copies} onChange={(e) => setFormData({ ...formData, total_copies: e.target.value })} className="w-full px-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" />
                          </div>
                        </div>
                      </div>
                    </div>
                    {imageMode === 'url' && (
                      <div>
                        <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Cover URL</label>
                        <input value={formData.cover_image} onChange={(e) => { setFormData({ ...formData, cover_image: e.target.value }); setPreviewUrl(e.target.value); }} className="w-full px-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs" placeholder="https://..." />
                      </div>
                    )}
                    <div>
                      <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Description</label>
                      <textarea rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all font-bold text-xs resize-none" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 md:px-10 md:py-6 border-t-2 border-white-400 bg-white-500 flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={onClose} className="flex-1 py-3.5 border-2 border-white-400 rounded-2xl font-black uppercase tracking-widest text-[10px] text-white-300">Cancel</button>
                <button type="submit" disabled={loading} className={`flex-[1.5] py-3.5 ${editBook ? 'bg-space_indigo' : 'bg-dark_cyan'} text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg flex items-center justify-center`}>
                  {loading ? <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" /> : editBook ? 'Update Volume Record' : 'Register Volume'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </AnimatePresence>

      <NewCategoryModal 
        isOpen={isNewCatModalOpen} 
        onClose={() => setIsNewCatModalOpen(false)} 
        onAdd={handleAddNewCategory} 
      />
    </>
  )
}

export default AddBookModal